import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { sitePhoto } from "@/lib/site-photos";
import heroImgAsset from "@/assets/hero-mariage-dj-1280.webp.asset.json";
import { HERO_HOME, MARIAGE_PHOTO, SETUP_PHOTO } from "@/lib/responsive-images";
import { useReveal } from "@/hooks/use-reveal";
import { CITY_SLUGS, CITIES } from "@/data/cities";
import { DateField } from "@/components/date-field";
import { BarParfumSection } from "@/components/bar-parfum-section";

import { lazy, Suspense } from "react";
const ShowreelSection = lazy(() => import("@/components/showreel-section").then((m) => ({ default: m.ShowreelSection })));
const GoogleReviewsCarousel = lazy(() => import("@/components/google-reviews-carousel").then((m) => ({ default: m.GoogleReviewsCarousel })));
const HomeSeoTail = lazy(() => import("@/components/home-seo-tail").then((m) => ({ default: m.HomeSeoTail })));
import materielProSetup from "@/assets/materiel-pro-setup.webp.asset.json";
import receptionImg from "@/assets/dj-mariage-reception.jpg";
import venueBastide from "@/assets/venue-bastide.jpg";
import venueChateau from "@/assets/venue-chateau.jpg";
import venueMas from "@/assets/venue-mas.jpg";
import { trackEvent, trackDevisEnvoye } from "@/lib/analytics";
import { useSectionView } from "@/hooks/use-section-view";
import djBooth480 from "@/assets/home/dj-booth-480.webp.asset.json";
import djBooth800 from "@/assets/home/dj-booth-800.webp.asset.json";
import djBooth1200 from "@/assets/home/dj-booth-1200.webp.asset.json";
import party480 from "@/assets/home/party-480.webp.asset.json";
import party800 from "@/assets/home/party-800.webp.asset.json";
import party1200 from "@/assets/home/party-1200.webp.asset.json";
import bride480 from "@/assets/home/bride-480.webp.asset.json";
import bride800 from "@/assets/home/bride-800.webp.asset.json";
import bride1200 from "@/assets/home/bride-1200.webp.asset.json";
import ceremony480 from "@/assets/home/ceremony-480.webp.asset.json";
import ceremony800 from "@/assets/home/ceremony-800.webp.asset.json";
import ceremony1200 from "@/assets/home/ceremony-1200.webp.asset.json";
import logoPioneer from "@/assets/brands/pioneerdj-com.webp.asset.json";
import logoChauvet from "@/assets/brands/chauvetdj-com.webp.asset.json";
import logoBose from "@/assets/brands/bose-com.webp.asset.json";
import logoRcf from "@/assets/brands/rcf-it.webp.asset.json";
import logoEv from "@/assets/brands/electrovoice-com.webp.asset.json";
import logoSennheiser from "@/assets/brands/sennheiser-com.webp.asset.json";
import logoAllenHeath from "@/assets/brands/allen-heath-com.webp.asset.json";
import logoShure from "@/assets/brands/shure-com.webp.asset.json";
import logoCameo from "@/assets/brands/cameolight-com.webp.asset.json";
import { metaDesc } from "@/lib/seo-description";




/** Images rapatriées en local (CDN même origine) — plus aucun appel à images.unsplash.com. */
const LOCAL = {
  djBooth: { src: djBooth800.url, srcSet: `${djBooth480.url} 480w, ${djBooth800.url} 800w, ${djBooth1200.url} 1200w` },
  party: { src: party800.url, srcSet: `${party480.url} 480w, ${party800.url} 800w, ${party1200.url} 1200w` },
  bride: { src: bride800.url, srcSet: `${bride480.url} 480w, ${bride800.url} 800w, ${bride1200.url} 1200w` },
  ceremony: { src: ceremony800.url, srcSet: `${ceremony480.url} 480w, ${ceremony800.url} 800w, ${ceremony1200.url} 1200w` },
};

// Images Unsplash — banque libre, thématique DJ / mariage / Provence
const IMG = {
  hero: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?fm=webp&auto=compress&fit=crop&w=1200&q=72",
  djBooth: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  reception: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  dance: "https://images.unsplash.com/photo-1530023367847-a683933f4172?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  lights: "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  concert: "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?fm=webp&auto=compress&fit=crop&w=1200&q=72",
  sparklers: "https://images.unsplash.com/photo-1519741497674-611481863552?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  vinyl: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  speakers: "https://images.unsplash.com/photo-1518972559570-7cc1309f3229?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  venue: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?fm=webp&auto=compress&fit=crop&w=1200&q=72",
  provence: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?fm=webp&auto=compress&fit=crop&w=1200&q=72",
  bride: "https://images.unsplash.com/photo-1606800052052-a08af7148866?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  cocktail: "https://images.invalid", // placeholder removed below
  glasses: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  ceremony: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?fm=webp&auto=compress&fit=crop&w=1000&q=72",
  party: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?fm=webp&auto=compress&fit=crop&w=1200&q=72",
  laser: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?fm=webp&auto=compress&fit=crop&w=1000&q=72",
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "DJ Val · DJ Avignon & Vaucluse, mariage dès 1490 €" },
      {
        name: "description",
        content:
          metaDesc("DJ Val, DJ pro en Provence depuis 20 ans : mariages dès 1 490 €, anniversaires, entreprise. Sono et lumière incluses, 41 avis Google, devis gratuit en 24 h.")
      },
      { name: "keywords", content: "DJ Val, DJ Provence, DJ Avignon, DJ Cavaillon, DJ Aix-en-Provence, DJ mariage Provence" },
      { property: "og:title", content: "DJ Val · DJ Avignon & Vaucluse, mariage dès 1490 €" },
      { property: "og:description", content: metaDesc("20 ans d'expérience · 200+ mariages · 41 avis Google. Sono et lumière incluses. Devis gratuit en 24 h.") },
      { property: "og:url", content: "https://dj-val.fr/" },
      { property: "og:image", content: `https://dj-val.fr${heroImgAsset.url}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "DJ Val · DJ Mariage & Événements Provence" },
      { name: "twitter:description", content: "20 ans d'expérience · 200+ mariages · Devis gratuit en 24 h." },
      { name: "twitter:image", content: `https://dj-val.fr${heroImgAsset.url}` },
    ],
    links: [
      { rel: "canonical", href: "https://dj-val.fr/" },
      { rel: "alternate", hrefLang: "fr-FR", href: "https://dj-val.fr/" },
      { rel: "alternate", hrefLang: "en", href: "https://dj-val.fr/en/wedding-dj-provence" },
      { rel: "alternate", hrefLang: "x-default", href: "https://dj-val.fr/" },
      {
        rel: "preload",
        as: "image",
        type: "image/avif",
        href: HERO_HOME.avifSrc,
        imageSrcSet: HERO_HOME.avifSrcSet,
        imageSizes: HERO_HOME.sizes,
        fetchPriority: "high",
      } as unknown as { rel: string; href: string },

    ],


    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "Combien coûte un DJ mariage dans le Vaucluse ?", acceptedAnswer: { "@type": "Answer", text: "À partir de 1 490 € pour un mariage (8 à 12 h de prestation), sonorisation Line Array et éclairage LED inclus. Formules transparentes tout-inclus, livraison & installation comprises sous 80 km de Cavaillon." } },
            { "@type": "Question", name: "Pourquoi un DJ pro plutôt qu'une playlist Spotify ?", acceptedAnswer: { "@type": "Answer", text: "Un DJ pro lit la piste, mixe sans coupure, gère le micro et adapte la programmation. Sur 200+ mariages animés, les soirées sans DJ pro se vident 90 min plus tôt." } },
            { "@type": "Question", name: "Quelle zone couvrez-vous ?", acceptedAnswer: { "@type": "Answer", text: "Vaucluse (84), Bouches-du-Rhône (13), Gard (30). Avignon, Aix, Arles, Nîmes, Cavaillon, Carpentras, Orange, Uzès, Salon. Déplacement offert sous 80 km de Cavaillon." } },
            { "@type": "Question", name: "Quand réserver ?", acceptedAnswer: { "@type": "Answer", text: "10 à 14 mois à l'avance pour mai–septembre en Provence. 1 seul mariage par week-end." } },
            { "@type": "Question", name: "Le matériel est-il fourni ?", acceptedAnswer: { "@type": "Answer", text: "Oui : Line Array RCF/JBL, 4–8 jeux LED, 2 micros HF Shure, Pioneer CDJ-3000 + DJM-A9. Installation et démontage inclus." } },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": "https://dj-val.fr/#webpage",
          url: "https://dj-val.fr/",
          name: "DJ Val · DJ mariage & événements en Provence",
          inLanguage: "fr-FR",
          isPartOf: { "@id": "https://dj-val.fr/#website" },
          about: { "@id": "https://dj-val.fr/#business" },
          primaryImageOfPage: `https://dj-val.fr${heroImgAsset.url}`,
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", "h2", ".speakable"],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "DJ Val — DJ Mariage & Événements en Provence",
          serviceType: "DJ Mariage & Événements",
          provider: { "@id": "https://dj-val.fr/#business" },

          areaServed: [
            { "@type": "AdministrativeArea", name: "Vaucluse" },
            { "@type": "AdministrativeArea", name: "Bouches-du-Rhône" },
            { "@type": "AdministrativeArea", name: "Gard" },
            { "@type": "Place", name: "Luberon" },
            { "@type": "Place", name: "Alpilles" },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Formules DJ Val",
            itemListElement: [
              { "@type": "Offer", name: "Formule Essentielle", price: "690", priceCurrency: "EUR", url: "https://dj-val.fr/tarifs" },
              { "@type": "Offer", name: "Formule Signature", price: "1490", priceCurrency: "EUR", url: "https://dj-val.fr/tarifs" },
              { "@type": "Offer", name: "Formule Prestige", price: "2190", priceCurrency: "EUR", url: "https://dj-val.fr/tarifs" },
            ],
          },
          // Pas de balisage avis auto-déclaré (règle Google "self-serving reviews").
        }),
      },
    ],
  }),
  component: HomePage,
});

const STATS = [
  { v: "200", s: "+", l: "Mariages animés" },
  { v: "500", s: "+", l: "Événements privés" },
  { v: "1000", s: "+", l: "Locations de matériel" },
  { v: "20", s: "ans", l: "D'expérience" },
];

const PROCESS = [
  { n: "01", t: "Premier contact", d: "Vous décrivez votre événement (date, lieu, invités, ambiance). Réponse personnalisée en moins de 24 h avec devis détaillé." },
  { n: "02", t: "Rendez-vous", d: "Visio ou rencontre à Cavaillon : on co-construit la playlist, le déroulé et les annonces micro (entrée, première danse, surprises)." },
  { n: "03", t: "Repérage", d: "Visite du lieu si nécessaire pour valider l'implantation sono, l'alimentation et la position des enceintes." },
  { n: "04", t: "Jour J", d: "Installation 3 h avant, balance son, briefing traiteur et photographe. Prestation continue sans coupure, démontage en fin de soirée." },
];

const TESTIMONIALS = [
  { name: "Camille & Antoine", event: "Mariage · Domaine de Manville, Les Baux", text: "Val a transformé notre dîner en moment magique. La piste de danse n'a pas désempli de 22h à 4h du matin. Tous nos invités demandent ses coordonnées." },
  { name: "Sophie L.", event: "40 ans · Mas à L'Isle-sur-la-Sorgue", text: "Très pro, ponctuel, à l'écoute. Le matériel est haut de gamme et le mix d'enfer. Une vraie valeur ajoutée par rapport à un DJ « lambda »." },
  { name: "Groupe Mistral SA", event: "Soirée d'entreprise · 180 personnes", text: "Briefing impeccable, du cocktail jazzy au club deep house en fin de soirée. Nous le reprenons pour 2026 sans hésiter." },
];

const FEATURED_VENUES: { name: string; line1: string; line2?: string; region: string; url: string }[] = [
  { name: "Domaine de Manville", line1: "Domaine de", line2: "Manville", region: "Les Baux-de-Provence", url: "https://www.domainedemanville.fr/" },
  { name: "Domaine de Fontenille", line1: "Domaine de", line2: "Fontenille", region: "Luberon", url: "https://www.domainedefontenille.fontenille-collection.com/" },
  { name: "Mas la Provence", line1: "Mas la", line2: "Provence", region: "Provence", url: "https://maslaprovence.fr/" },
];

const PARTNER_VENUES = [
  "Mas de l'Isle-sur-la-Sorgue",
  "Château de Roussan",
  "Bastide de Gordes",
  "Mas des Herbes Blanches",
  "Château La Coste",
  "Domaine de la Verrière",
];



const EVENT_TYPES = ["Mariage", "Anniversaire", "Entreprise", "Soirée privée", "Autre"];

function QuickContactForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    type: "Mariage",
    city: "",
    message: "",
    hp: "", // honeypot
  });
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const [sending, setSending] = useState(false);
  const navigate = useNavigate();


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (form.hp) return; // silent bot rejection
    if (!consent) {
      setError("Merci d'accepter la politique de confidentialité.");
      trackEvent("form_error", { form_name: "quick_contact", error_field: "consent" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Adresse e-mail invalide.");
      trackEvent("form_error", { form_name: "quick_contact", error_field: "email" });
      return;
    }
    setSending(true);
    try {
      // Chargé à la demande : la fonction serveur ne pèse plus sur le bundle initial.
      const { submitLead } = await import("@/lib/leads.functions");
      await submitLead({
        data: {
          nom: form.name,
          email: form.email,
          telephone: form.phone || null,
          type_event: form.type,
          date_event: form.date || null,
          lieu: form.city || null,
          message: form.message || null,
          source: "accueil" as const,
          hp: form.hp || null,
        },
      });
      setSent(true);
      trackEvent("generate_lead", { form_name: "quick_contact", prestation: form.type });
      trackDevisEnvoye("quick_contact", form.type);
      void navigate({ to: "/merci" });
    } catch (err) {
      const detail = err instanceof Error && err.message ? ` (${err.message})` : "";
      setError(
        `Votre demande n'a pas pu être envoyée${detail}. Réessayez dans un instant, ou appelez directement le 07 67 90 86 67 / info.djval@gmail.com.`,
      );
      trackEvent("form_error", { form_name: "quick_contact", error_field: "network" });
    } finally {
      setSending(false);
    }
  };



  return (
    <form
      onSubmit={onSubmit}
      className="relative bg-white/[0.06] md:backdrop-blur-xl border border-white/20 p-6 sm:p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] text-white"
      aria-label="Formulaire de contact rapide"
      style={{ backdropFilter: "blur(4px)" }}
    >
      <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--gold)] to-transparent" aria-hidden />
      <p className="text-[10px] uppercase tracking-[0.32em] text-[var(--gold)] font-semibold">
        Devis express
      </p>
      <h2 className="font-display text-3xl sm:text-4xl mt-2 leading-[0.95] text-white">
        Votre devis en <span className="italic text-[var(--gold)]">24 h</span>
      </h2>
      <span className="gold-rule !w-10 my-4 block !bg-[var(--gold)] !opacity-100" />

      <div className="space-y-3">
        <input
          required
          maxLength={80}
          value={form.name}
          onChange={update("name")}
          placeholder="Nom *"
          aria-label="Votre nom"
          className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-[var(--gold)] transition-colors"
         data-clarity-mask="true"/>
        <div className="grid grid-cols-2 gap-3">
          <input
            required
            type="tel"
            maxLength={20}
            value={form.phone}
            onChange={update("phone")}
            placeholder="Téléphone *"
            aria-label="Votre téléphone"
            className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-[var(--gold)] transition-colors"
           data-clarity-mask="true"/>
          <input
            required
            type="email"
            maxLength={120}
            value={form.email}
            onChange={update("email")}
            placeholder="E-mail *"
            aria-label="Votre e-mail"
            className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-[var(--gold)] transition-colors"
           data-clarity-mask="true"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <select
            value={form.type}
            onChange={update("type")}
            aria-label="Type d'événement"
            className="bg-white/5 border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--gold)] transition-colors [&>option]:bg-[var(--ink)]"
          >
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <DateField
            value={form.date}
            onChange={(iso) => setForm((f) => ({ ...f, date: iso }))}
            id="hero-date-evenement"
            labelClassName="mb-1 block text-[10px] uppercase tracking-[0.2em] text-white/60"
            className="w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white/80 placeholder:text-white/40 focus:outline-none focus:border-[var(--gold)] transition-colors"
          />

        </div>
      </div>

      {/* Honeypot invisible */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={form.hp}
        onChange={update("hp")}
        className="absolute -left-[9999px] w-0 h-0 opacity-0"
        aria-hidden="true"
       data-clarity-mask="true"/>

      <label className="mt-4 flex items-start gap-2 text-[11px] text-white/70 leading-snug cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-[var(--gold)]"
          required
          data-clarity-mask="true"
        />
        <span>
          J'accepte que mes données soient utilisées pour traiter ma demande —{" "}
          <Link to="/politique-confidentialite" className="underline hover:text-[var(--gold)]">politique de confidentialité</Link>.
        </span>
      </label>

      {error && (
        <p role="alert" className="text-[11px] text-red-300 mt-2">{error}</p>
      )}

      <button
        type="submit"
        disabled={sending || sent}
        className="mt-4 w-full bg-[var(--gold)] text-[var(--ink)] py-3.5 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60"
      >
        {sending ? "Envoi en cours…" : "Envoyer ma demande →"}
      </button>


      {sent && (
        <p className="text-[11px] text-[var(--gold)] mt-3 text-center">
          Merci ! Votre demande est bien enregistrée, réponse sous 24 h.
        </p>
      )}

      <p className="mt-4 text-center text-[10px] tracking-[0.12em] text-white/55 leading-relaxed">
        Réponse sous 24 h · Un seul événement par week-end · Devis gratuit et sans engagement
      </p>

      <p className="mt-3 text-center text-[10px] uppercase tracking-[0.28em] text-white/50">
        ou <a href="tel:0767908667" onClick={() => trackEvent("contact_click", { contact_method: "tel", cta_location: "home" })} className="text-[var(--gold)] hover:text-white transition-colors" style={{ fontFamily: "Arial, sans-serif", fontVariantNumeric: "tabular-nums" }}>07 67 90 86 67</a>
      </p>
    </form>
  );
}


function HomePage() {
  useReveal();
  const heroRef = useSectionView<HTMLElement>("hero");
  const prestationsRef = useSectionView<HTMLElement>("prestations");
  const materielRef = useSectionView<HTMLElement>("materiel");
  const temoignagesRef = useSectionView<HTMLElement>("temoignages");
  const contactRef = useSectionView<HTMLElement>("contact");

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      {/* ===================== HERO ===================== */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-center px-6 overflow-hidden bg-[var(--ink)]">

        {/* Background : image fixe (LCP-friendly) */}
        <div className="absolute inset-0 z-0">
          <picture>
            <source type="image/avif" srcSet={HERO_HOME.avifSrcSet} sizes={HERO_HOME.sizes} />
            <img
              src={HERO_HOME.src}
              srcSet={HERO_HOME.srcSet}
              sizes={HERO_HOME.sizes}
              width={HERO_HOME.width}
              height={HERO_HOME.height}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover"
              fetchPriority="high"
              loading="eager"
              decoding="async"
            />
          </picture>



          {/* Overlay dark éditorial */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(115deg, rgba(8,10,18,0.88) 0%, rgba(8,10,18,0.62) 45%, rgba(8,10,18,0.35) 70%, rgba(8,10,18,0.80) 100%)",
            }}
          />
          {/* Grain */}
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.8'/></svg>\")",
            }}
            aria-hidden
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full grid md:grid-cols-12 gap-10 lg:gap-14 items-center py-24 md:py-16">
          <div className="md:col-span-7 space-y-6 hero-in">
            <p className="text-[10px] uppercase tracking-[0.4em] font-semibold text-[var(--gold)] flex items-center gap-3">
              <span className="w-8 h-px bg-[var(--gold)]" />
              DJ Mariage · Provence
            </p>


            <h1
              className="hero-oversize text-white"
              style={{ textWrap: "balance" as any }}
              aria-label="L'émotion d'une soirée inoubliable."
            >
              <span className="block">L'émotion</span>
              <span className="block italic text-[var(--gold)]">d'une soirée</span>
              <span className="block">inoubliable.</span>
            </h1>

            <p
              className="text-base sm:text-lg text-white font-light leading-relaxed max-w-lg"
            >
              20 ans d'expérience · 200+ mariages animés en Vaucluse, Luberon & Alpilles · <span className="text-[var(--gold)]">un seul événement par week-end</span>, pour vous consacrer entièrement.
            </p>

            <div className="flex items-center gap-6 pt-2 text-white text-[11px] uppercase tracking-[0.28em]">
              <span className="flex items-center gap-1.5">
                <span className="text-[var(--gold)]">★★★★★</span> 5,0 · Google
              </span>
              <span className="hidden sm:inline w-px h-4 bg-white/40" />
              <span className="hidden sm:inline">SACEM · Hiscox · SIRET</span>
            </div>

            {/* Indicateur de rareté / saison */}
            <div className="inline-flex items-center gap-2 mt-2 px-3 py-1.5 border border-[var(--gold)]/40 bg-[var(--gold)]/5 text-[10px] uppercase tracking-[0.25em] text-[var(--gold)]">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--gold)] animate-pulse" aria-hidden="true" />
              Saison 2026 / 2027 · Calendrier en cours de réservation
            </div>

          </div>

          {/* Quick contact form — right side */}
          <div className="md:col-span-5 hero-in hero-in-delay">
            <QuickContactForm />
          </div>

        </div>

        {/* Scroll indicator — CTA engagement */}
        <Link
          to="/contactez-nous"
          onClick={() => trackEvent("cta_click", { cta_label: "Vérifier mes disponibilités", cta_location: "hero_scroll_indicator" })}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 group"
          aria-label="Vérifier mes disponibilités"
        >
          <span className="text-[9px] uppercase tracking-[0.4em] text-white/70 group-hover:text-[var(--gold)] transition-colors">Vérifier mes disponibilités</span>
          <span
            className="block w-px h-10 bg-gradient-to-b from-[var(--gold)] to-transparent scroll-pulse"
            style={{ transformOrigin: "top" }}
          />

        </Link>


      </section>

      <BarParfumSection />

      {/* ===================== TRUST STRIP — note Google + garanties pro ===================== */}
      <section aria-label="Preuves de confiance" className="bg-[var(--ink)] text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <a
            href="https://www.google.com/search?q=DJ+Val+Cavaillon+avis" onClick={() => trackEvent("outbound_click", { link_url: "https://www.google.com/search?q=DJ+Val+Cavaillon+avis", cta_location: "home_reviews" })}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 group"
            aria-label="Voir les avis Google de DJ Val"
          >
            <span className="flex" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} width="18" height="18" viewBox="0 0 24 24" fill="var(--gold)">
                  <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18.2 22 12 18.3 5.8 22l1.7-7.2L2 10l7.1-1.1z" />
                </svg>
              ))}
            </span>
            <span className="font-[Arial] tabular-nums text-lg leading-none">5,0 / 5</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-white/60 group-hover:text-[var(--gold)] transition-colors">
              41 avis Google vérifiés
            </span>
          </a>
          <div className="hidden md:block h-8 w-px bg-white/15" />
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.25em] text-white/70">
            {[
              { label: "Assurance RC Pro Hiscox" },
              { label: "Déclaré SACEM / SPRÉ" },
              { label: "SIRET · Cavaillon 84" },
              { label: "Devis sous 24 h" },
            ].map((b) => (
              <li key={b.label} className="flex items-center gap-2">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                <span>{b.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===================== AVIS GOOGLE — CARROUSEL (mise en avant) ===================== */}
      <Suspense fallback={<div className="min-h-[30vh]" />}><GoogleReviewsCarousel /></Suspense>

      {/* ===================== STATS COUNTERS ===================== */}
      <section className="border-y border-foreground/10 bg-card">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map((s, i) => (
            <div key={s.l} className="sr" data-reveal style={{ transitionDelay: `${i * 80}ms` }}>
              <p className="font-serif-display text-4xl md:text-5xl text-foreground leading-none">
                {s.v}<span className="text-3xl">{s.s}</span>
              </p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-foreground/85 mt-2">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===================== SHOWREEL VIDÉO ===================== */}
      <Suspense fallback={<div className="min-h-[40vh]" />}><ShowreelSection /></Suspense>



      {/* ===================== LIEUX PARTENAIRES ===================== */}
      <section aria-label="Lieux de réception où DJ Val intervient" className="relative overflow-hidden border-y border-foreground/10 bg-card/40">
        {/* Filigrane photos Provence — décoratif */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="grid grid-cols-3 h-full opacity-[0.18]">
            <div className="bg-cover bg-center" style={{ backgroundImage: `url(${venueBastide})` }} />
            <div className="bg-cover bg-center hidden sm:block" style={{ backgroundImage: `url(${venueChateau})` }} />
            <div className="bg-cover bg-center hidden md:block" style={{ backgroundImage: `url(${venueMas})` }} />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-card/85 via-card/70 to-card/85 backdrop-blur-md" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-14">
          <div className="text-center mb-10 sr" data-reveal>
            <p className="eyebrow">Ils nous ont fait confiance</p>
            <h2 className="font-serif-display text-2xl sm:text-3xl text-foreground mt-3 leading-tight">
              Domaines & lieux de réception <span className="italic">de Provence</span>
            </h2>
          </div>

          {/* Wordmarks vedettes — 3 domaines partenaires */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-10 items-center justify-items-center mb-10 pb-10 border-b border-foreground/10">
            {FEATURED_VENUES.map((v, i) => (
              <a
                key={v.name}
                href={v.url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                aria-label={`${v.name} — site officiel (nouvelle fenêtre)`}
                className="group block text-center opacity-70 hover:opacity-100 transition-opacity sr"
                data-reveal
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <span className="block font-serif-display text-foreground text-xl sm:text-2xl leading-tight tracking-wide">
                  {v.line1}
                </span>
                {v.line2 && (
                  <span className="block font-serif-display italic text-foreground/90 text-lg sm:text-xl leading-tight mt-0.5">
                    {v.line2}
                  </span>
                )}
                <span className="mt-2 inline-block h-px w-8 bg-[var(--gold)] transition-all group-hover:w-16" />
                <span className="block text-[9px] uppercase tracking-[0.3em] text-muted-foreground mt-2">
                  {v.region}
                </span>
              </a>
            ))}
          </div>

          {/* Autres lieux — liste texte */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4 items-center justify-items-center">
            {PARTNER_VENUES.map((v, i) => (
              <span
                key={v}
                className="font-serif-display italic text-foreground/85 text-base sm:text-lg text-center leading-tight sr"
                data-reveal
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                {v}
              </span>
            ))}
          </div>
          <p className="text-center mt-8 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            + 80 lieux référencés dans le Vaucluse, les Alpilles et le Luberon
          </p>
        </div>
      </section>

      {/* OÙ J'INTERVIENS */}
      <section className="py-20 px-6 border-t border-[var(--gold-line)]">
        <div className="max-w-5xl mx-auto text-center">
          <p className="eyebrow text-[var(--gold)]">Où j'interviens</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Basé à Cavaillon, <span className="italic">présent dans toute la Provence</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {[
              { to: "/dj-avignon", label: "DJ à Avignon" },
              { to: "/dj-vaucluse", label: "DJ Vaucluse" },
              { to: "/dj-mariage/luberon", label: "DJ mariage Luberon" },
              { to: "/dj-mariage/alpilles", label: "DJ mariage Alpilles" },
              { to: "/dj-mariage/aix-en-provence", label: "DJ mariage Aix-en-Provence" },
              { to: "/dj-mariage/bouches-du-rhone", label: "DJ mariage Bouches-du-Rhône" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-5 py-2.5 border border-[var(--gold-line)] bg-card text-foreground/90 text-xs uppercase tracking-[0.15em] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>








      {/* ===================== INTRO — IMAGE LEFT / TEXT RIGHT ===================== */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="relative sr-left" data-reveal>
            <img
              src={sitePhoto("djRegie", LOCAL.djBooth.src)}
              srcSet={LOCAL.djBooth.srcSet}
              sizes="(max-width: 768px) 100vw, 50vw"
              width={800}
              height={1000}
              decoding="async"
              alt="DJ Val derrière sa table Pioneer DJM-A9 lors d'un mariage"
              className="w-full aspect-[4/5] object-cover"
              loading="lazy"
            />
            <div className="absolute -bottom-6 -right-6 bg-[var(--gold)] text-[var(--ink)] px-8 py-6 max-w-[220px]">
              <p className="font-serif-display italic text-3xl">2010</p>
              <p className="text-[10px] uppercase tracking-[0.25em] mt-1">Depuis 20 ans en Provence</p>
            </div>
            <div className="absolute -top-6 -left-6 w-32 h-32 border-2 border-[var(--gold)] -z-10" />
          </div>

          <div className="sr-right" data-reveal>
            <p className="eyebrow">À propos de Val</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4 leading-tight">
              DJ professionnel,<br/>
              <span className="italic text-foreground">artisan du dancefloor</span>
            </h2>
            <span className="gold-rule !w-12 my-8" />
            <p className="text-base text-foreground/85 leading-loose font-light">
              Installé à Cavaillon au cœur du Vaucluse, j'anime depuis 20 ans les
              mariages, soirées privées et événements d'entreprise de toute la
              Provence. Je travaille exclusivement avec du matériel <strong>Pioneer Professional</strong>,
              <strong> RCF</strong> et <strong>Shure</strong>.
            </p>
            <p className="text-base text-foreground/85 leading-loose font-light mt-5">
              Chaque prestation est unique : rendez-vous de préparation, playlist
              co-construite, repérage du lieu. Mon objectif&nbsp;: que vos invités
              dansent jusqu'au bout de la nuit.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-3 text-[10px] uppercase tracking-[0.25em] text-foreground/85">
              {["Assurance RC Pro Hiscox", "Déclaré SACEM / SPRÉ", "SIRET 84300 Cavaillon"].map((c) => (
                <span key={c} className="border border-foreground/10 px-3 py-3 text-center">{c}</span>
              ))}
            </div>
            <Link
              to="/a-propos"
              onClick={() => trackEvent("cta_click", { cta_label: "Mon parcours complet", cta_location: "home_about" })}
              className="mt-10 inline-block text-[11px] uppercase tracking-[0.3em] text-foreground border-b border-[var(--gold)] pb-1 hover:text-foreground hover:border-foreground transition-colors"
            >
              Mon parcours complet →
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== PARALLAX QUOTE ===================== */}
      <section
        className="parallax-bg relative min-h-[60vh] flex items-center justify-center px-6"
        style={{ backgroundImage: `url(${IMG.concert})` }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 max-w-3xl text-center sr-zoom" data-reveal>
          <p className="font-serif-display italic text-white/80 text-6xl leading-none">"</p>
          <p className="font-serif-display text-white text-2xl sm:text-4xl leading-tight font-light italic">
            Un mariage réussi, c'est une piste qui ne se vide pas.
            Mon métier&nbsp;: lire la salle et donner le bon morceau à la seconde près.
          </p>
          <span className="block gold-rule !w-16 mx-auto my-8" />
          <p className="text-[11px] uppercase tracking-[0.32em] text-white/80">— Val, DJ depuis 20 ans</p>
        </div>
      </section>

      {/* ===================== UNIVERS — BIG CARDS ===================== */}
      <section ref={prestationsRef} className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-7 sr-left" data-reveal>
            <p className="eyebrow">Mes prestations</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-foreground mt-4 leading-[1.05]">
              Trois <span className="italic text-foreground">expertises</span><br/>
              un seul interlocuteur
            </h2>
          </div>
          <p className="md:col-span-5 text-foreground/85 font-light leading-relaxed sr-right" data-reveal>
            Mariage, événement privé ou entreprise, location de matériel pur :
            chaque demande est traitée avec le même niveau d'exigence technique
            et de préparation humaine.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {[
            { title: "Mariages", to: "/dj-mariage", img: MARIAGE_PHOTO.src, srcSet: MARIAGE_PHOTO.srcSet, sizes: MARIAGE_PHOTO.sizes, n: "01", badge: "Dès 1 490 €", text: "Cocktail, dîner, ouverture de bal et dancefloor. Préparation personnalisée et coordination avec votre wedding planner." },
            { title: "Soirées & Entreprise", to: "/soirees-evenements", img: sitePhoto("soireeEntreprise", LOCAL.party.src), srcSet: LOCAL.party.srcSet, sizes: "(max-width: 768px) 100vw, 33vw", n: "02", badge: "Dès 690 €", text: "Anniversaires, EVJF/EVG, baptêmes, séminaires. Du cocktail d'affaires à la soirée club avec effets fluo et CO₂." },
            { title: "Sono & Location", to: "/location-materiel", img: SETUP_PHOTO.src, srcSet: SETUP_PHOTO.srcSet, sizes: SETUP_PHOTO.sizes, n: "03", badge: "Devis < 24 h", text: "Line Array, éclairage LED, lasers, machine à fumée, photobooth. Livraison, montage et reprise sur tout le Vaucluse." },
          ].map((u, i) => (
            <Link
              key={u.title}
              to={u.to}
              onClick={() => trackEvent("cta_click", { cta_label: u.title, cta_location: "home_prestations" })}
              className="group block sr"
              data-reveal
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={u.img}
                  srcSet={u.srcSet}
                  sizes={u.sizes}
                  width={640}
                  height={853}
                  alt={`${u.title} — prestation DJ Val Provence`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent transition-opacity group-hover:from-black/80" />
                <span className="absolute top-6 left-6 font-serif-display italic text-foreground text-xl">
                  {u.n}
                </span>
                <span className="absolute top-6 right-6 text-[10px] uppercase tracking-[0.25em] text-[var(--ink)] font-semibold bg-[var(--gold)] px-3 py-1.5">
                  {u.badge}
                </span>
                <div className="absolute bottom-0 inset-x-0 p-8">
                  <h3 className="font-serif-display text-3xl text-white">{u.title}</h3>
                  <span className="block gold-rule !w-8 my-4 transition-all group-hover:!w-16" />
                  <p className="text-sm text-white/85 leading-relaxed font-light">{u.text}</p>
                  <span className="mt-5 inline-block text-[10px] uppercase tracking-[0.3em] text-foreground group-hover:text-white transition-colors">
                    Découvrir →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===================== ASYMMETRIC — TEXT LEFT / IMAGE RIGHT ===================== */}
      <section ref={materielRef} className="bg-card border-y border-foreground/10 py-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="sr-left order-2 md:order-1" data-reveal>
            <p className="eyebrow">Matériel</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4 leading-tight">
              Du matériel pro,<br/>
              <span className="italic text-foreground">parce que ça s'entend</span>
            </h2>
            <span className="gold-rule !w-12 my-8" />
            <p className="text-base text-foreground/85 leading-loose font-light">
              Je travaille exclusivement avec les grandes marques de la scène pro —
              son cristallin, éclairage précis, fiabilité totale. Le parc est
              dimensionné selon le nombre d'invités et la nature du lieu.
            </p>
            <div className="mt-10 grid grid-cols-3 border-t border-l border-foreground/10">
              {[
                { name: "Pioneer DJ", logo: logoPioneer.url },
                { name: "Chauvet", logo: logoChauvet.url },
                { name: "Bose", logo: logoBose.url },
                { name: "RCF", logo: logoRcf.url },
                { name: "Electro-Voice", logo: logoEv.url },
                { name: "Sennheiser", logo: logoSennheiser.url },
                { name: "Allen & Heath", logo: logoAllenHeath.url },
                { name: "Shure", logo: logoShure.url },
                { name: "Cameo", logo: logoCameo.url },
              ].map((brand) => (
                <div
                  key={brand.name}
                  className="group relative flex items-center justify-center aspect-[4/3] bg-background border-r border-b border-foreground/10 p-6 sm:p-8 transition-colors hover:bg-foreground/[0.02]"
                  title={brand.name}
                >
                  <img
                    src={brand.logo}
                    alt={`Logo ${brand.name}`}
                    loading="lazy"
                    decoding="async"
                    width={320}
                    height={320}
                    className="max-h-16 sm:max-h-[72px] w-auto object-contain opacity-90 group-hover:opacity-100 transition-all duration-300"
                  />
                </div>
              ))}
            </div>
            <p className="mt-6 text-[10px] uppercase tracking-[0.25em] text-muted-foreground text-center">
              Marques partenaires · matériel professionnel
            </p>
            <Link
              to="/sono-lumiere"
              onClick={() => trackEvent("cta_click", { cta_label: "Parc matériel complet", cta_location: "home_materiel" })}
              className="mt-10 inline-block text-[11px] uppercase tracking-[0.3em] text-foreground border-b border-[var(--gold)] pb-1 hover:text-foreground hover:border-foreground transition-colors"
            >
              Le parc matériel complet →
            </Link>
          </div>

          <div className="relative order-1 md:order-2 sr-right" data-reveal>
            <figure className="relative">
              <img
                src={materielProSetup.url}
                alt="Régie DJ Pioneer, line array RCF et éclairage lyres LED installés pour un mariage en Provence au coucher du soleil"
                className="w-full aspect-[4/5] object-cover rounded-lg shadow-xl"
                loading="lazy"
                width={1024}
                height={1280}
              />
              <figcaption className="mt-3 text-center text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Installation type · mariage en Provence
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ===================== PARALLAX FULL BLEED — DESTINATIONS ===================== */}
      <section
        className="parallax-bg relative min-h-[70vh] flex items-center px-6"
        style={{ backgroundImage: `url(${receptionImg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto w-full grid md:grid-cols-2">
          <div className="sr-left" data-reveal>
            <p className="eyebrow text-white/70">Zones d'intervention</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-white mt-4 leading-tight">
              D'Avignon à<br/>
              <span className="italic text-white/80">Aix-en-Provence</span>
            </h2>
            <span className="gold-rule !w-12 my-8" />
            <p className="text-base text-white/85 leading-loose font-light max-w-xl">
              Basé à Cavaillon (84), je couvre sans frais de déplacement un
              rayon de 80 km : Vaucluse, Bouches-du-Rhône, Gard et Drôme
              provençale. Au-delà, un forfait kilométrique transparent.
            </p>
            <div className="mt-10 flex flex-wrap gap-2">
              {CITY_SLUGS.map((s) => (
                <Link
                  key={s}
                  to="/dj-mariage/$ville"
                  params={{ ville: s }}
                  onClick={() => trackEvent("cta_click", { cta_label: CITIES[s].name, cta_location: "home_zones" })}
                  className="border border-white/30 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-white hover:bg-[var(--gold)] hover:text-[var(--ink)] hover:border-foreground transition-colors"
                >
                  {CITIES[s].name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PROCESS — TIMELINE ===================== */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-8 mb-20 items-end">
          <div className="md:col-span-6 sr-left" data-reveal>
            <p className="eyebrow">Ma méthode</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-foreground mt-4 leading-[1.05]">
              4 étapes,<br/>
              <span className="italic text-foreground">zéro mauvaise surprise</span>
            </h2>
          </div>
          <p className="md:col-span-6 text-foreground/85 font-light leading-relaxed sr-right" data-reveal>
            De la première prise de contact au démontage en fin de soirée,
            une méthode rodée sur 200+ mariages. Pas de stress, pas d'oubli :
            chaque étape a son rendez-vous.
          </p>
        </div>

        <div className="grid gap-px bg-foreground/10 border border-foreground/10 md:grid-cols-4">
          {PROCESS.map((p, i) => (
            <div key={p.n} className="bg-background p-8 sr" data-reveal style={{ transitionDelay: `${i * 100}ms` }}>
              <p className="font-serif-display text-5xl italic text-foreground/85 leading-none">{p.n}</p>
              <span className="block gold-rule !w-8 my-5" />
              <h3 className="font-serif-display text-2xl text-foreground">{p.t}</h3>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed font-light">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===================== IMAGE LEFT / TEXT RIGHT — MARIAGE ===================== */}
      <section className="bg-card border-y border-foreground/10 py-24 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div className="relative sr-left" data-reveal>
            <img src={LOCAL.bride.src} srcSet={LOCAL.bride.srcSet} sizes="(max-width: 768px) 100vw, 40vw" alt="Mariée en robe blanche entrée dans la salle" className="w-full aspect-[4/5] object-cover" loading="lazy" decoding="async" width={1000} height={1250} />
            <img src={LOCAL.ceremony.src} srcSet={LOCAL.ceremony.srcSet} sizes="(max-width: 768px) 60vw, 25vw" alt="Cérémonie laïque en Provence" className="absolute -bottom-12 -right-8 w-2/3 aspect-square object-cover border-8 border-card hidden md:block" loading="lazy" decoding="async" width={1000} height={1000} />
          </div>
          <div className="sr-right md:pl-12" data-reveal>
            <p className="eyebrow">Spécialité mariage</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4 leading-tight">
              Votre <span className="italic text-foreground">jour J</span><br/>
              mérite mieux qu'une playlist
            </h2>
            <span className="gold-rule !w-12 my-8" />
            <p className="text-base text-foreground/85 leading-loose font-light">
              Cérémonie laïque sonorisée, vin d'honneur jazzy, entrée des
              mariés théâtralisée, ouverture de bal préparée pas à pas,
              clubbing maîtrisé jusqu'à 4 h. Je gère aussi les annonces, les
              jeux et la coordination avec votre traiteur et votre photographe.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-foreground/90 font-light">
              {[
                "Rendez-vous de préparation et playlist co-construite",
                "Repérage du lieu et validation technique",
                "2 micros HF Shure pour cérémonie et discours",
                "Éclairage architectural et dancefloor",
                "1 seul mariage par week-end — préparation maximale",
              ].map((b) => (
                <li key={b} className="flex gap-3"><span className="text-foreground">→</span> {b}</li>
              ))}
            </ul>
            <Link to="/dj-mariage" onClick={() => trackEvent("cta_click", { cta_label: "Toutes les formules mariage", cta_location: "home_mariage" })} className="mt-10 inline-block bg-[var(--gold)] text-[var(--ink)] px-8 py-4 text-[11px] uppercase tracking-[0.3em] font-semibold hover:bg-foreground hover:text-white transition-colors">
              Toutes les formules mariage
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section ref={temoignagesRef} className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7 sr-left" data-reveal>
            <p className="eyebrow">Avis vérifiés</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-foreground mt-4 leading-[1.05]">
              Ce que disent<br/>
              <span className="italic text-foreground">les mariés</span>
            </h2>
          </div>
          <div className="md:col-span-5 sr-right" data-reveal>
            <p className="text-foreground tracking-[0.3em] text-2xl">★★★★★</p>
            <p className="text-foreground/85 font-light mt-2">5/5 · 41 avis Google · 100% recommandation</p>
          </div>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <figure key={t.name} className="sr border border-foreground/10 bg-card p-10 relative" data-reveal style={{ transitionDelay: `${i * 120}ms` }}>
              <span className="absolute -top-6 left-8 font-serif-display italic text-foreground text-7xl leading-none">"</span>
              <p className="text-foreground tracking-[0.3em] mt-2">★★★★★</p>
              <blockquote className="mt-5 text-base text-foreground/85 leading-relaxed font-light italic">
                {t.text}
              </blockquote>
              <figcaption className="mt-8 pt-6 border-t border-foreground/10">
                <p className="font-serif-display text-lg text-foreground">{t.name}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">{t.event}</p>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <a
            href="https://www.google.com/search?q=DJ+Val+Cavaillon+avis" onClick={() => trackEvent("outbound_click", { link_url: "https://www.google.com/search?q=DJ+Val+Cavaillon+avis", cta_location: "home_reviews" })}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 border border-foreground/20 hover:border-[var(--gold)] px-8 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors group"
            aria-label="Lire les 41 avis Google de DJ Val"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.09-1.92 3.27-4.75 3.27-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
            <span className="group-hover:text-[var(--gold)] transition-colors">Voir les 41 avis Google</span>
            <span className="text-[var(--gold)]">★★★★★</span>
          </a>
        </div>
      </section>


      {/* ===================== MARQUEE ===================== */}
      <section className="border-y border-foreground/10 bg-foreground text-background py-6 overflow-hidden">
        <div className="marquee-track whitespace-nowrap text-foreground">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="font-serif-display italic text-3xl mx-8">
              Avignon · Cavaillon · Aix-en-Provence · Arles · Nîmes · Carpentras · L'Isle-sur-la-Sorgue · Orange ·
            </span>
          ))}
        </div>
      </section>




      {/* ===================== CTA — PARALLAX ===================== */}
      <section
        ref={contactRef}
        className="parallax-bg relative py-24 px-6"
        style={{ backgroundImage: `url(${IMG.venue})` }}
      >
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative z-10 max-w-3xl mx-auto text-center sr-zoom" data-reveal>
          <p className="eyebrow text-white/70">Prêt à réserver ?</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-white mt-4 leading-tight">
            Parlons de<br/>
            <span className="italic gold-text-shine">votre soirée</span>
          </h2>
          <span className="gold-rule !w-16 mx-auto my-8" />
          <p className="text-base text-white/85 leading-loose font-light">
            Décrivez-moi votre projet en quelques mots. Je reviens vers vous
            sous 24 h avec un devis détaillé, sans engagement.
          </p>
          <div className="pt-10 flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link
              to="/contactez-nous"
              onClick={() => trackEvent("cta_click", { cta_label: "Demander un devis", cta_location: "home_final_cta" })}
              className="bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-colors"
            >
              Demander un devis
            </Link>
            <Link
              to="/tarifs"
              onClick={() => trackEvent("cta_click", { cta_label: "Voir les tarifs", cta_location: "home_final_cta" })}
              className="border border-white/40 text-white px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:border-foreground hover:text-foreground transition-colors"
            >
              Voir les tarifs
            </Link>
          </div>
        </div>
      </section>

      <Suspense fallback={null}>
        <HomeSeoTail />
      </Suspense>

    </div>
  );
}
